import { useEffect, useState, useRef, useCallback } from "react";
import { LensSDKException } from "lens-platform-sdk";
import { useAnalytics } from "src/hooks/useAnalytics";
import { useLensPlatformClient } from "./useLensPlatformClient";

/**
 * Hook for add emails to a user account
 */
export const useAddEmails = () => {
  const lensPlatformClient = useLensPlatformClient();
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<Awaited<ReturnType<typeof lensPlatformClient.user.createEmails>>>();
  const [error, setError] = useState<LensSDKException>();
  const { track, trackError } = useAnalytics();
  // Keep up with mounted/unmounted state
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const addEmails = useCallback(
    async (emails: string[]) => {
      let lensSDKException: LensSDKException | undefined;

      try {
        if (mounted.current) {
          setError(undefined);
          setLoading(true);
        }
        const response = await lensPlatformClient.user.createEmails(emails);

        if (mounted.current) {
          track("Added Emails to Account");
          setResponse(response);
        }
      } catch (exception) {
        if (exception instanceof LensSDKException) {
          trackError("Add Emails to Account Failed");
          setError(exception);
          lensSDKException = exception;
        }
      } finally {
        if (mounted.current) {
          setLoading(false);
        }
      }

      return lensSDKException;
    },
    [lensPlatformClient.user, track, trackError],
  );

  return {
    addEmails,
    error,
    response,
    loading,
  };
};
